import { IconFile, IconPaperclip, IconX } from "@tabler/icons-react";
import { useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { toast } from "sonner";
import {
  CUSTOMER_NOTE_ATTACHMENT_MAX_BYTES,
  CUSTOMER_NOTE_ATTACHMENT_MAX_FILES,
} from "../../api/queries/customerOrganizationQueries";
import { formatFileSize } from "../../utils/utilsFunctions/functions";
import { isAcceptedCustomerNoteFile } from "./customerNoteAttachments";

type CustomerNoteAttachmentPickerProps = {
  files: File[];
  onChange: (files: File[]) => void;
  disabled?: boolean;
};

export function CustomerNoteAttachmentPicker({
  files,
  onChange,
  disabled = false,
}: CustomerNoteAttachmentPickerProps) {
  const isAtMaxFiles = files.length >= CUSTOMER_NOTE_ATTACHMENT_MAX_FILES;
  const dropzoneDisabled = disabled || isAtMaxFiles;

  const appendFiles = useCallback(
    (incoming: File[]) => {
      if (incoming.length === 0) return;

      const accepted = incoming.filter((file) => {
        if (!isAcceptedCustomerNoteFile(file)) {
          toast.error(`${file.name}: only images and PDF files are allowed.`);
          return false;
        }
        if (file.size > CUSTOMER_NOTE_ATTACHMENT_MAX_BYTES) {
          toast.error(
            `${file.name}: file is too large (max ${formatFileSize(CUSTOMER_NOTE_ATTACHMENT_MAX_BYTES)}).`,
          );
          return false;
        }
        return true;
      });

      if (accepted.length === 0) return;

      const existingKeys = new Set(
        files.map((file) => `${file.name}-${file.size}`),
      );
      const uniqueNewFiles = accepted.filter(
        (file) => !existingKeys.has(`${file.name}-${file.size}`),
      );

      if (uniqueNewFiles.length === 0) return;

      const combined = [...files, ...uniqueNewFiles];
      if (combined.length > CUSTOMER_NOTE_ATTACHMENT_MAX_FILES) {
        toast.error(
          `You can attach up to ${CUSTOMER_NOTE_ATTACHMENT_MAX_FILES} files per note.`,
        );
        onChange(combined.slice(0, CUSTOMER_NOTE_ATTACHMENT_MAX_FILES));
        return;
      }

      onChange(combined);
    },
    [files, onChange],
  );

  const handleIncomingFiles = useCallback(
    (incoming: File[]) => {
      appendFiles(incoming);
    },
    [appendFiles],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleIncomingFiles,
    onDropRejected: (rejections) => {
      handleIncomingFiles(rejections.map((rejection) => rejection.file));
    },
    accept: {
      "image/*": [],
      "application/pdf": [".pdf"],
    },
    maxSize: CUSTOMER_NOTE_ATTACHMENT_MAX_BYTES,
    multiple: true,
    disabled: dropzoneDisabled,
    noKeyboard: dropzoneDisabled,
  });

  const handleRemove = (indexToRemove: number) => {
    onChange(files.filter((_, index) => index !== indexToRemove));
  };

  return (
    <div className="space-y-2">
      <div
        {...getRootProps()}
        className={`rounded-lg border-2 border-dashed px-4 py-5 text-center transition-colors ${
          dropzoneDisabled
            ? "cursor-not-allowed border-slate-200 bg-slate-50 opacity-60"
            : isDragActive
              ? "cursor-copy border-blue-400 bg-blue-50"
              : "cursor-pointer border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50"
        }`}
      >
        <input {...getInputProps()} />
        <div className="flex flex-col items-center gap-2">
          <div
            className={`flex h-9 w-9 items-center justify-center rounded-full ${
              isDragActive ? "bg-blue-100" : "bg-slate-100"
            }`}
          >
            <IconPaperclip
              size={18}
              className={isDragActive ? "text-blue-600" : "text-slate-500"}
            />
          </div>
          <p className="text-sm font-medium text-slate-700">
            {isAtMaxFiles
              ? `Maximum of ${CUSTOMER_NOTE_ATTACHMENT_MAX_FILES} files reached`
              : isDragActive
                ? "Drop files here"
                : "Drag and drop files here, or click to browse"}
          </p>
          <p className="text-xs text-slate-500">
            Images and PDFs only, up to {CUSTOMER_NOTE_ATTACHMENT_MAX_FILES}{" "}
            files ({formatFileSize(CUSTOMER_NOTE_ATTACHMENT_MAX_BYTES)} each).
          </p>
        </div>
      </div>
      {files.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {files.map((file, index) => (
            <div
              key={`${file.name}-${file.size}-${index}`}
              className="inline-flex max-w-full items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-xs text-slate-700"
            >
              <IconFile size={14} className="shrink-0 text-slate-500" />
              <span
                className="max-w-[12rem] truncate font-medium"
                title={file.name}
              >
                {file.name}
              </span>
              <span className="shrink-0 text-slate-400">
                {formatFileSize(file.size)}
              </span>
              <button
                type="button"
                onClick={() => handleRemove(index)}
                disabled={disabled}
                className="rounded p-0.5 text-slate-500 hover:bg-white hover:text-red-600 disabled:opacity-50"
                aria-label={`Remove ${file.name}`}
                title="Remove"
              >
                <IconX size={14} />
              </button>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
